import {
  Input,
  Button,
  Typography,
  Textarea,
} from "@material-tailwind/react";
import Toaster from "../../components/Toaster";
import { useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeftIcon, PlusCircleIcon, XCircleIcon } from "@heroicons/react/24/solid";
import * as Yup from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import ShowLoader from "@/components/Loader";
import Select from 'react-select';
import { addBanner, resetStatus } from "@/store/slices/bannerSlice";
import FilePreview from "@/components/FilePreview";
import ScrollToTop from "@/components/ScrollToTop";
import { useTranslation } from "react-i18next";

const noHTMLRegex = /^(?!.*<[^>]*>).*$/;
const noCodeRegex = /^(?!.*(javascript:|<script|<\/script>|<style|<\/style>|<iframe|<\/iframe>)).*$/i;
const notBlankRegex = /\S/;
const noConsecutiveSpecialChars = /^(?!.*([!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?])\1).*$/;
const noLeadingTrailingPunct = /^(?!^[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?])(?<![!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]$).*$/;

const getFormSchema = (t) => Yup.object().shape({
    title: Yup.string()
      .trim()
      .required(t("validations.title.required"))
      .min(3, t("validations.title.min"))
      .max(40, t("validations.title.max"))
      .matches(notBlankRegex, t("validations.title.notBlank"))
      .matches(noHTMLRegex, t("validations.title.noHTML"))
      .matches(noCodeRegex, t("validations.title.noCode"))
      .matches(noConsecutiveSpecialChars, t("validations.title.noConsecutiveSpecialChars"))
      .matches(noLeadingTrailingPunct, t("validations.title.noLeadingTrailingPunct")),

    sub_title: Yup.string()
      .trim()
      .required(t("validations.sub_title.required"))
      .min(3, t("validations.sub_title.min"))
      .max(50, t("validations.sub_title.max"))
      .matches(notBlankRegex, t("validations.sub_title.notBlank"))
      .matches(noHTMLRegex, t("validations.sub_title.noHTML"))
      .matches(noCodeRegex, t("validations.sub_title.noCode"))
      .matches(noConsecutiveSpecialChars, t("validations.sub_title.noConsecutiveSpecialChars"))
      .matches(noLeadingTrailingPunct, t("validations.sub_title.noLeadingTrailingPunct")),

    description: Yup.string()
      .trim()
      .required(t("validations.description.required"))
      .min(10, t("validations.description.min"))
      .max(100, t("validations.description.max"))
      .matches(notBlankRegex, t("validations.description.notBlank"))
      .matches(noHTMLRegex, t("validations.description.noHTML"))
      .matches(noCodeRegex, t("validations.description.noCode"))
      .matches(noConsecutiveSpecialChars, t("validations.description.noConsecutiveSpecialChars"))
      .matches(noLeadingTrailingPunct, t("validations.description.noLeadingTrailingPunct")),

    image: Yup.mixed()
      .required(t("validations.image.required"))
      .test("fileType", t("validations.image.fileType"), (value) => {
        if (!value || !(value instanceof File)) return true;
        return value.type.startsWith("image/");
      })
      .test("fileSize", t("validations.image.fileSize"), (value) => {
        if (!value || !(value instanceof File)) return true;
        return value.size <= 2 * 1024 * 1024;
      }),

    status: Yup.string()
      .oneOf(["active", "inactive"], t("validations.status.invalid"))
      .required(t("validations.status.required")),
  });

export function AddBanner({ isEdit = false, isView = false }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams();

  const { t } = useTranslation("banner");
  const { t: ct } = useTranslation("common");

  const selectedBanner = useSelector((s) => s.banner.selected);
  const { loading } = useSelector((state) => state.banner);

  const initialForm = {
    title: "",
    sub_title: "",
    description: "",
    image: null,
    status: "active",
    // priority:''
  };

  const formSchema = useMemo(() => getFormSchema(t), [t]);
  const statusOptions = useMemo(() => [
    { label: t('statusOptions.active'), value: 'active' },
    { label: t('statusOptions.inactive'), value: 'inactive' }
  ], [t]);

  const { register, handleSubmit, setValue, watch, reset, formState: { errors } } = useForm({
    resolver: yupResolver(formSchema),
    defaultValues: initialForm
  });

  useEffect(() => {
    if (isEdit) {
      if (selectedBanner && String(selectedBanner.id) === String(id)) {
        reset({ ...selectedBanner });
      }
    } else {
      reset(initialForm);
    }
  }, [isEdit, id, selectedBanner, reset]);

  const onSubmit = async (data) => {
    try {
      const result = await dispatch(addBanner(data)).unwrap();
      if (result.status) {
        Toaster.success(isEdit ? t('toaster.updateSuccess') : t('toaster.addSuccess'));
        navigate(`/dashboard/banners`);
      } else {
        Toaster.error(result.message || t("toaster.somethingWentWrong"));
      }
    } catch (err) {
      Toaster.error(err?.message || t("toaster.addFailed"));
    } finally {
      dispatch(resetStatus());
    }
  };

  const bannerImage = watch('image');

  const handleFileChange = (e) => {
    let file = e.target.files[0];
    setValue('image', file, { shouldValidate: true });
  }

  const removeImage = () => {
    setValue('image', null, { shouldValidate: true });
  }

  return (
    <div className="mt-5 mb-8 flex flex-col gap-8 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      {loading && <ShowLoader />}
      <ScrollToTop />
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-2">
        <div className="sticky top-0 z-30 mt-1 mb-6 p-5 bg-blue-gray-f5">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 md:gap-2 ">
            <div className="flex items-center gap-2 cursor-pointer">
              <Typography variant="h5" color="blue-gray" className="font-extrabold text-gray-800">
                {isView ? t('viewBanner') : isEdit ? t('editBanner') : t('addBanner')}
              </Typography>
            </div>
            {!isView &&
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outlined"
                  onClick={() => navigate(`/dashboard/banners`)}
                  className="rounded-lg"
                >
                  {t('cancel')}
                </Button>
                <Button
                  type="submit"
                  disabled={loading}
                  className="flex items-center gap-2 border-gray-300 capitalize font-bold bg-black text-white btn-material">
                  {loading ? t("saving") : isEdit ? t("update") : t("save")}
                </Button>
              </div>
            }
          </div>
          <div className="flex items-center gap-1">
            <Link to={`/dashboard/banners`} className="flex items-center text-blue-600 hover:text-blue-800">
              <ArrowLeftIcon className="h-4 w-4 mr-1" />
              {t('back')}
            </Link>
          </div>
        </div>
        <fieldset disabled={isView}>
          <div className="mt-1 mb-6 border border-blue-gray-100 rounded-lg p-5 bg-white ">
            <div className="mb-5 pb-2 border-b border-blue-gray-200">
              <Typography variant="h4" color="blue-gray">
                {t('bannerDetails')}
              </Typography>
            </div>
            <div className="grid grid-cols-1 gap-6 xl:grid-cols-1">
              <div className="md:col-span-2 flex flex-col gap-3">
                <Typography variant="small" color="blue-gray" className="font-semibold text-gray-700">
                  {t('bannerTitle')} <span className="text-red-500">*</span>
                </Typography>
                <Input
                  type="text"
                  placeholder={t('bannerTitlePlaceholder')}
                  className="!border !border-gray-300 bg-white text-gray-900 shadow-sm focus:ring-2 focus:ring-blue-500/50 focus:!border-blue-500"
                  labelProps={{ className: "hidden" }}
                  containerProps={{ className: "min-w-full" }}
                  {...register('title')}
                  error={Boolean(errors.title)}
                />
                {errors.title && <p className="text-red-500 text-sm mt-1">{errors.title.message}</p>}
              </div>
              <div className="md:col-span-2 flex flex-col gap-3">
                <Typography variant="small" color="blue-gray" className="font-semibold text-gray-700">
                  {t('bannerSubTitle')} <span className="text-red-500">*</span>
                </Typography>
                <Input
                  type="text"
                  placeholder={t('bannerSubTitlePlaceholder')}
                  className="!border !border-gray-300 bg-white text-gray-900 shadow-sm focus:ring-2 focus:ring-blue-500/50 focus:!border-blue-500"
                  labelProps={{ className: "hidden" }}
                  containerProps={{ className: "min-w-full" }}
                  {...register('sub_title')}
                  error={Boolean(errors.sub_title)}
                />
                {errors.sub_title && <p className="text-red-500 text-sm mt-1">{errors.sub_title.message}</p>}
              </div>

              {/* Priority Field */}
              {/* <div className="flex flex-col gap-2">
                <Typography variant="small" color="blue-gray" className="font-semibold text-gray-700">
                  Display Priority (Optional)
                </Typography>
                <Input
                  type="number"
                  placeholder="e.g. 1"
                  className="!border !border-gray-300 bg-white text-gray-900 shadow-sm focus:ring-2 focus:ring-blue-500/50 focus:!border-blue-500"
                  labelProps={{ className: "hidden" }}
                  containerProps={{ className: "min-w-full" }}
                  {...register('priority')}
                  error={Boolean(errors.priority)}
                />
                {errors.priority && <p className="text-red-500 text-sm mt-1">{errors.priority.message}</p>}
              </div> */}

              <div className="md:col-span-2 flex flex-col gap-3">
                <Typography variant="small" color="blue-gray" className="font-semibold text-gray-700">
                  {t('status')} <span className="text-red-500">*</span>
                </Typography>
                <Select
                  placeholder={t('selectBannerStatus')}
                  onChange={(status) => setValue('status', status?.value, { shouldValidate: true })}
                  value={statusOptions?.find(opt => opt.value === watch('status')) || null}
                  options={statusOptions}
                  isClearable
                  isDisabled={isView}
                />
                {errors.status && <p className="text-red-500 text-sm mt-1">{errors.status.message}</p>}
              </div>

              <div className="md:col-span-2 flex flex-col gap-2">
                <Typography variant="small" color="blue-gray" className="font-semibold text-gray-700">
                  {t('bannerDescription')} <span className="text-red-500">*</span>
                </Typography>
                <Textarea
                  rows={4}
                  placeholder={t('bannerDescriptionPlaceholder')}
                  className="!border !border-gray-300 bg-white text-gray-900 shadow-sm focus:ring-2 focus:ring-blue-500/50 focus:!border-blue-500"
                  labelProps={{ className: "hidden" }}
                  containerProps={{ className: "min-w-full" }}
                  {...register('description')}
                  error={Boolean(errors.description)}
                />
                {errors.description && <p className="text-red-500 text-sm mt-1">{errors.description.message}</p>}
              </div>

              <div className="md:col-span-2 flex flex-col gap-3">
                <Typography variant="small" color="blue-gray" className="font-semibold text-gray-700">
                  {t('bannerImage')} <span className="text-red-500">*</span>
                </Typography>
                <div className="flex flex-wrap items-end gap-6">
                  {/* {bannerImage && (
                    <div className="relative w-60 h-36 border-2 border-gray-300 rounded-lg overflow-hidden shadow-md group">
                      <img
                        src={bannerImage instanceof File ? URL.createObjectURL(bannerImage) : bannerImage || ''}
                        alt="Banner Preview"
                        className="w-full h-full object-cover"
                      />
                      
                      {!isView && <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <XCircleIcon
                          onClick={removeImage}
                          className="h-10 w-10 text-red-400 cursor-pointer hover:text-red-600 transition-colors"
                        />
                      </div>}
                    </div>
                  )} */}
                  {!isView && <label htmlFor="bannerImage" className="cursor-pointer">
                    <input
                      id="bannerImage"
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleFileChange}
                    />
                    <div className="h-36 w-60 bg-white border-2 border-dashed border-blue-400 text-blue-600 rounded-lg hover:bg-blue-50 transition-colors flex items-center justify-center shadow-sm">
                      <div className="flex flex-col items-center justify-center text-center p-4">
                        <PlusCircleIcon className="h-10 w-10 mb-2 text-blue-500" />
                        <p className="text-base font-medium">{t('uploadImage')}</p>
                      </div>
                    </div>
                  </label>}
                  {bannerImage && <FilePreview
                    file={bannerImage}
                    onRemove={() => removeImage()}
                    isView={isView}
                    containerClass="relative w-60 h-36 rounded-md overflow-hidden border border-gray-300"
                  />}
                </div>
                {errors.image && 
                  <p className="text-red-500 text-sm mt-2">{errors.image.message}</p>
                }
              </div>
            </div>
          </div>
        </fieldset>
      </form>
    </div>
  );
}

export default AddBanner;